import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps, useHistory, useLocation } from "react-router";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { selectOrganization } from "go-security/services/organizations/selectors";
import AppFormatResourceStatusLabel from "../../../../../../../../../../components/FormatStatus";
import { PurchaseApi } from "../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../services/Api/api";
import PurchaseBasicInfoCard from "./PurchaseBasicInfoCard";
import PurchaseItemsCard from "./PurchaseItemsCard/PurchaseItemsCard";

interface ImportedResource {
	resourceFromFile: PurchaseApi;
	uploadedFile?: File;
}

const PurchasePreviewFromImport: FC<RouteComponentProps> = (props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const {
		state: { resourceFromFile, uploadedFile },
	} = useLocation<ImportedResource>();
	const { t } = useTranslation();
	const { addFlash } = useFlash();
	const history = useHistory();
	const organization = useSelector(selectOrganization);

	const renderSubtitle = () => (
		<ul>
			<li>{FormatDate(resourceFromFile.purchased_at)}</li>
			<li>{FormatMoney(resourceFromFile.total_price_net_money)}</li>
		</ul>
	);

	const renderTitle = () => (
		<>
			{t("modules.purchase.header.preview.title")} {resourceFromFile.document_number.replace(" ", "- ")}
			<AppFormatResourceStatusLabel status={resourceFromFile.status} />
			{resourceFromFile.lock_status === "LOCKED" && (
				<AppFormatResourceStatusLabel status={resourceFromFile.lock_status} />
			)}
		</>
	);

	const avatar = {
		color: "#FF9900",
		date: resourceFromFile.purchased_at,
	};

	const handleSavePurchaseFromPreview = async () => {
		if (!uploadedFile) return;
		try {
			setLoading(true);
			const data = new FormData();
			data.set("file", uploadedFile);
			const res = await api.organization().savePurchaseFromFile(data);
			setLoading(false);
			history.push(`/${organization.id}/documents/purchases/${res.id}`);
		} catch (err) {
			handleError.alert(err, addFlash);
			setLoading(false);
		}
	};

	return (
		<div className="content-preview">
			<Header
				title={renderTitle()}
				avatar={avatar}
				subtitle={renderSubtitle()}
				tags={resourceFromFile?.tags?.map((tag) => tag.name)}
			/>
			<div className="content-preview-body">
				<PurchaseBasicInfoCard purchase={resourceFromFile} fileImportPreviewMode />
				<PurchaseItemsCard purchase={resourceFromFile} fileImportPreviewMode />
				<div className={"content-preview-sticky-footer"}>
					<ButtonLoading loading={loading} onClick={handleSavePurchaseFromPreview}>
						{t("common.action.save", { ns: "lib" })}
					</ButtonLoading>
				</div>
			</div>
		</div>
	);
};

export default PurchasePreviewFromImport;
